import type { MasterDie, Rarity } from '../core/types';
import { RARITY_NAME, RARITY_ORDER } from '../data/machines';
import { DEFAULT_DICE_VIEW, filterDice, sortDice, type DiceView } from './diceView';
import { readSessionPref, writeSessionPref } from './sessionPref';

/**
 * Onglet Dés en quatre sous-onglets (v0.50.0, COMMENTS v0.49.1 §2, A100-3) —
 * la **logique pure** : sous-onglet mémorisé, regroupement de l'Inventaire en
 * volets par rareté, état d'ouverture des volets. Aucun DOM ici ; l'écran est
 * `screens/accalmie/dice.ts`.
 */

// ───────────────────────────────────────────────────────────── Sous-onglets

export type DiceSubtab = 'equipped' | 'inventory' | 'recycler' | 'spells';

export const DICE_SUBTABS: readonly DiceSubtab[] = ['equipped', 'inventory', 'recycler', 'spells'];

export const DICE_SUBTAB_LABEL: Record<DiceSubtab, string> = {
  equipped: 'Équipés',
  inventory: 'Inventaire',
  recycler: 'Recycleur',
  spells: 'Sorts',
};

const SUBTAB_KEY = 'diceordie.dice.subtab';
const PANELS_KEY = 'diceordie.dice.rarityPanels';
const RECYCLE_PANELS_KEY = 'diceordie.dice.recyclePanels';

/** Sous-onglet mémorisé pour la session ; « Équipés » par défaut (ou si la valeur est inconnue). */
export function readDiceSubtab(): DiceSubtab {
  const raw = readSessionPref(SUBTAB_KEY);
  return DICE_SUBTABS.includes(raw as DiceSubtab) ? (raw as DiceSubtab) : 'equipped';
}

export function writeDiceSubtab(tab: DiceSubtab): void {
  writeSessionPref(SUBTAB_KEY, tab);
}

// ─────────────────────────────────────────────────── Inventaire en volets

/** Du plus rare au plus commun (Prismatique en tête). */
export const RARITIES_RAREST_FIRST: readonly Rarity[] = [...RARITY_ORDER].reverse();

/**
 * Rang d'un dé dans un volet : d100 (debug) avant d20, puis d12, d6, d4. Une
 * autre face (jamais livrée aujourd'hui) vient après le d4, de la plus grande à
 * la plus petite — l'ordre ne dépend pas d'une liste à tenir à jour.
 */
export function sidesRank(sides: number): number {
  const known = [100, 20, 12, 6, 4].indexOf(sides);
  return known >= 0 ? known : 5 + (1000 - sides) / 1000;
}

export interface RarityPanel {
  rarity: Rarity;
  /** « Légendaire · 3 ». */
  title: string;
  dice: MasterDie[];
  /** Ouvert (état mémorisé, sinon : le premier volet non vide). */
  open: boolean;
}

/** Titre d'un volet : nom de la rareté et nombre de dés (« Légendaire · 3 »). */
export function rarityPanelTitle(rarity: Rarity, count: number): string {
  return `${RARITY_NAME[rarity]} · ${count}`;
}

/** Niveau d'un dé pour le tri : un dé de campagne (sans niveau) vaut 0 (même lecture que `abyssApi.dieLevelOf`). */
export function sortLevelOf(die: Pick<MasterDie, 'level'>): number {
  const level = die.level;
  return typeof level === 'number' && level > 0 ? level : 0;
}

/**
 * Tri d'un volet (v0.53.0, A103 : **niveau décroissant d'abord**) : niveau
 * du dé (un dé de campagne vaut 0), puis d100, d20, d12, d6, d4, puis score
 * décroissant (l'ordre d'arrivée départage). La rareté est celle du volet.
 */
export function sortWithinPanel(dice: readonly MasterDie[], score: (die: MasterDie) => number): MasterDie[] {
  return dice
    .map((die, index) => ({ die, index, level: sortLevelOf(die), rank: sidesRank(die.sides), score: score(die) }))
    .sort((a, b) => b.level - a.level || a.rank - b.rank || b.score - a.score || a.index - b.index)
    .map((e) => e.die);
}

/** États d'ouverture mémorisés : une rareté absente n'a pas encore été touchée par le joueur. */
export type PanelStates = Partial<Record<Rarity, boolean>>;

/**
 * Regroupe les dés **non équipés** par rareté, du plus rare au plus commun, les
 * volets vides omis. Dans un volet : niveau décroissant (A103), puis d20, d12,
 * d6, d4 (un d100 de debug avant le d20) puis score. Le premier volet non vide est ouvert tant que le joueur n'a
 * pas touché au sien ; un état mémorisé l'emporte. `firstOpen: false` : tout est
 * fermé par défaut (recyclage à l'unité).
 *
 * v0.55.8 (A119) : `view` (facultative, défaut = tri par niveau sans filtre)
 * filtre les dés **avant** le regroupement — un volet vide par le filtre est
 * omis, le titre compte les dés restants — et trie chaque volet par sa clé.
 */
export function groupInventoryByRarity(
  dice: readonly MasterDie[],
  equippedIds: ReadonlySet<string>,
  score: (die: MasterDie) => number,
  stored: PanelStates = {},
  firstOpen = true,
  view: DiceView = DEFAULT_DICE_VIEW,
): RarityPanel[] {
  const spare = filterDice(
    dice.filter((d) => !equippedIds.has(d.id)),
    view,
  );
  const panels: RarityPanel[] = [];
  for (const rarity of RARITIES_RAREST_FIRST) {
    const inRarity = spare.filter((d) => d.rarity === rarity);
    if (inRarity.length === 0) continue;
    panels.push({
      rarity,
      title: rarityPanelTitle(rarity, inRarity.length),
      dice: sortDice(inRarity, view, score),
      open: stored[rarity] ?? (firstOpen && panels.length === 0),
    });
  }
  return panels;
}

/**
 * **Révélation** d'un tirage (v0.52.0, COMMENTS v0.51.0 §2, A102) : les dés
 * obtenus en volets par rareté, du plus rare au plus commun, vides omis.
 * v0.53.0 (A103) : à l'intérieur d'un volet, le tri par défaut des dés
 * (`sortWithinPanel` : niveau, faces, score) remplace l'ordre du cœur ; sans
 * `score`, le niveau puis les faces seuls (score constant). Ouverts :
 * le premier volet (le plus rare) et celui qui porte le meilleur dé (`best`),
 * les autres fermés ; aucune mémoire de session (une fenêtre par tirage).
 */
export function groupRevealByRarity(
  dice: readonly MasterDie[],
  best: MasterDie | null | undefined,
  score: (die: MasterDie) => number = () => 0,
): RarityPanel[] {
  const panels: RarityPanel[] = [];
  for (const rarity of RARITIES_RAREST_FIRST) {
    const inRarity = dice.filter((d) => d.rarity === rarity);
    if (inRarity.length === 0) continue;
    panels.push({
      rarity,
      title: rarityPanelTitle(rarity, inRarity.length),
      dice: sortWithinPanel(inRarity, score),
      open: panels.length === 0 || (!!best && inRarity.includes(best)),
    });
  }
  return panels;
}

// ───────────────────────────────────────────── Mémoire de session des volets

function readStates(key: string): PanelStates {
  const raw = readSessionPref(key);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: PanelStates = {};
    for (const r of RARITY_ORDER) {
      const v = (parsed as Record<string, unknown>)[r];
      if (typeof v === 'boolean') out[r] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeState(key: string, rarity: Rarity, open: boolean): void {
  writeSessionPref(key, JSON.stringify({ ...readStates(key), [rarity]: open }));
}

/** Volets de l'Inventaire : ouverts / fermés par le joueur pendant la session. */
export const readInventoryPanels = (): PanelStates => readStates(PANELS_KEY);
export const writeInventoryPanel = (rarity: Rarity, open: boolean): void => writeState(PANELS_KEY, rarity, open);

/** Volets du recyclage à l'unité (fermés tant que le joueur ne les ouvre pas). */
export const readRecyclePanels = (): PanelStates => readStates(RECYCLE_PANELS_KEY);
export const writeRecyclePanel = (rarity: Rarity, open: boolean): void => writeState(RECYCLE_PANELS_KEY, rarity, open);
